import React, { useId } from 'react';
import { AIcon } from '../../atoms/AIcon/index';
import { AImage } from '../../atoms/AImage/index';
import { ASubheader } from '../../atoms/ASubheader/index';
import { ASubheaderSize } from '../../atoms/ASubheader/ASubheader.types';
import { Wrapper, HeaderRow, HeaderRowImage, HeaderRowText, Toggle, ContentArea } from './MAccordionItem.styles';
import { type MAccordionItemProps, MAccordionItemType } from './MAccordionItem.types';
import { ABody } from '../../atoms/ABody';
import type { AImageProps } from '../../atoms/AImage/AImage.types';
import { type AIconProps, AIconSize } from '../../atoms/AIcon/AIcon.types';
import { type ABodyProps, ABodySize } from '../../atoms/ABody/ABody.types';
import type { ASubheaderProps } from '../../atoms/ASubheader/ASubheader.types';
import clsx from 'clsx';
import { AspectRatio, MediaQuery, ResponsiveImageSpec, generateImageRenditions } from '../../utils/ImageUtils';

const RESPONSIVE_IMAGE_SPECS: ResponsiveImageSpec[] = [
  {
    media: MediaQuery.MIN_WIDTH_XS,
    aspectRatio: AspectRatio.STANDARD_HOR,
    imgWidthPx: 100,
  },
];

const COMPONENT_NAME = 'm-web-ui-kit-MAccordionItem';
const TEST_ID = 'web-ui-kit-MAccordionItem';

// TODO: Get I18n keys for these hardcoded tokens
const TEXT_I18N_EXPAND = 'Expand';
const TEXT_I18N_COLLAPSE = 'Collapse';

function MAccordionItem({
  type = MAccordionItemType.Icon,
  expanded = false,
  aImage = {},
  aIcon = {},
  aSubheader = {},
  aBody = {},
  onToggle,
  children,
  ...rest
}: MAccordionItemProps) {
  const id = useId();
  const headerTextId = `${id}-header-text`;

  const _aSubheader = {
    ...aSubheader,
    size: ASubheaderSize.L,
    className: clsx(aSubheader.className, 'm-accordion-item-header-text-heading'),
  };
  const _aBody = {
    ...aBody,
    size: ABodySize.XS,
    className: clsx(aBody.className, 'm-accordion-item-header-text-body'),
  };
  const _aIcon = {
    size: AIconSize.L,
    ...aIcon,
    className: clsx(aIcon.className, 'a-icon-branded'),
  };
  const _aImage = {
    ...aImage,
    imgClass: clsx(aImage.imgClass, 'm-accordion-item-image'),
    renditions: generateImageRenditions(RESPONSIVE_IMAGE_SPECS, aImage.src),
  };
  return (
    <Wrapper data-component-name={COMPONENT_NAME} data-testid={TEST_ID} {...rest}>
      <HeaderRow>
        {type === MAccordionItemType.Image && _aImage.src && (
          <HeaderRowImage>
            <AImage {...(_aImage as AImageProps)} />
          </HeaderRowImage>
        )}
        {type === MAccordionItemType.Icon && _aIcon.icon && <AIcon {...(_aIcon as AIconProps)} />}
        <HeaderRowText id={headerTextId}>
          {_aSubheader.text && <ASubheader {...(_aSubheader as ASubheaderProps)} />}
          {_aBody.bodyText && <ABody {...(_aBody as ABodyProps)} />}
        </HeaderRowText>
        <Toggle onClick={() => onToggle?.()} aria-expanded={expanded} aria-labelledby={headerTextId}>
          <AIcon aria-hidden="true" icon={expanded ? 'icon-arrow-up' : 'icon-arrow-down'} size={AIconSize.L} />
          <span className="sr-only">{expanded ? TEXT_I18N_COLLAPSE : TEXT_I18N_EXPAND}</span>
        </Toggle>
      </HeaderRow>
      {expanded && <ContentArea>{children}</ContentArea>}
    </Wrapper>
  );
}

export { MAccordionItem, MAccordionItemType, TEST_ID as M_ACCORDION_ITEM_TEST_ID };
